import React, { useRef, useEffect } from 'react';

/**
 * SoundLevelBar component
 * Displays a vertical bar showing the current audio input level
 */
const SoundLevelBar = ({ level = 0, textColor, width = 30, height = 80 }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    // Clear canvas
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Normalize level to 0-1 range (level comes as RMS which is typically 0-1)
    // Apply amplification factor to make the level more visible
    // RMS values are typically very small (0.01-0.1 for normal input)
    // Amplify by 5x to make the visualization match system audio meters better
    const amplifiedLevel = level * 5;
    const normalizedLevel = Math.min(Math.max(amplifiedLevel, 0), 1);
    
    // Calculate bar height
    const barHeight = normalizedLevel * canvasHeight;
    
    // Draw level bar from bottom up
    if (barHeight > 0) {
      // Create gradient from green (low) to yellow (medium) to red (high)
      const gradient = ctx.createLinearGradient(0, canvasHeight, 0, 0);
      gradient.addColorStop(0, '#00ff00');    // Green at bottom
      gradient.addColorStop(0.6, '#ffff00');  // Yellow at 60%
      gradient.addColorStop(1, '#ff0000');    // Red at top
      
      ctx.fillStyle = gradient;
      ctx.fillRect(2, canvasHeight - barHeight, canvasWidth - 4, barHeight);
    }

    // Draw tick marks
    ctx.strokeStyle = textColor;
    ctx.lineWidth = 1;
    const numTicks = 5;
    for (let i = 0; i <= numTicks; i++) {
      const y = (i / numTicks) * canvasHeight;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(5, y);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(canvasWidth - 5, y);
      ctx.lineTo(canvasWidth, y);
      ctx.stroke();
    }
  }, [level, textColor]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{
        border: `1px solid ${textColor}`,
        borderRadius: '4px',
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
      }}
    />
  );
};

export default SoundLevelBar;
